import type { CardListItem } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { CardListFilter, CatalogueKind } from "../ops/cards-ops";

/** The Stock list as it was left for a card: what Back shows while it reads the list again. */
export interface StockListPlace {
  readonly shopId: string;
  readonly filter: CardListFilter;
  readonly kind: CatalogueKind;
  readonly search: string;
  readonly items: readonly CardListItem[];
  readonly nextCursor: string | null;
  readonly scrollY: number;
  /** The card opened from it, which Back gives the focus to. */
  readonly openedCardId: string;
  /** The counts the rows showed, tapped and not yet read back. */
  readonly stockEdits: Readonly<Record<string, number>>;
}

/** How many places are kept, for Back to the entries they were left from. */
export const KEPT_PLACES = 10;

/** Where the Stock list was left for each card opened, so Back lands on the same cards at the same place, as on iOS. */
export class StockListRepository extends ChangeNotifier {
  readonly #places = new Map<string, StockListPlace>();
  /** The browser keeps an entry's mark across a reload, so ids carry this load's own token. */
  readonly #load = Math.random().toString(36).slice(2);
  #lastId = 0;
  readonly #left = new Map<string, Promise<void>>();

  /** The place an open kept under `id`, the mark its Stock entry carries; null once forgotten. */
  place(id: string): StockListPlace | null {
    return this.#places.get(id) ?? null;
  }

  /** The writes pages left on their way, per card, each until all of them land: the next Stock page waits for them. */
  get left(): ReadonlyMap<string, Promise<void>> {
    return this.#left;
  }

  /** `landed` settles, never rejects, once the card's write is answered or lost. */
  leaving(cardId: string, landed: Promise<void>): void {
    const earlier = this.#left.get(cardId);
    const all = earlier ? Promise.all([earlier, landed]).then(() => undefined) : landed;
    this.#left.set(cardId, all);
    void all.then(() => {
      if (this.#left.get(cardId) === all) this.#left.delete(cardId);
    });
    this.notifyListeners();
  }

  /** Keeps a place and answers its id; the oldest goes once more than `KEPT_PLACES` are kept. */
  remember(place: StockListPlace): string {
    const id = `${this.#load}-${++this.#lastId}`;
    this.#places.set(id, place);
    for (const old of this.#places.keys()) {
      if (this.#places.size <= KEPT_PLACES) break;
      this.#places.delete(old);
    }
    this.notifyListeners();
    return id;
  }

  reset(): void {
    this.#places.clear();
    this.#left.clear();
    this.notifyListeners();
  }
}
